import React from 'react';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Box } from '@mui/material';
import { BasicButton } from '@payer-admin-ui/components-v2/src/buttons/BasicButton';
import { RoleCheck } from '../../RoleCheck/RoleCheck';
import { ConfirmActionModal } from '../../Modal/ConfirmActionModal';
import { Role } from '../../../../components-v2/src/types/Role';
import { DefaultActionCard } from './DefaultActionCard';

const checkIfInternationalCompany = countryCode => countryCode && 'SE' !== countryCode;

export const IdentityActionCard = ({ actions, action, company, showManualActions, shouldShowData, headerProps }) => {
  const { t } = useTranslation();
  const [isApproveDialogOpen, setIsApproveDialogOpen] = React.useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = React.useState(false);

  const toggleApproveDialogOpen = React.useCallback(() => {
    setIsApproveDialogOpen(!isApproveDialogOpen);
  }, [isApproveDialogOpen, setIsApproveDialogOpen]);

  const toggleRejectDialogOpen = React.useCallback(() => {
    setIsRejectDialogOpen(!isRejectDialogOpen);
  }, [isRejectDialogOpen, setIsRejectDialogOpen]);

  const approve = React.useCallback(() => {
    setIsApproveDialogOpen(false);
    actions.approve();
  }, [actions, setIsApproveDialogOpen]);

  const reject = React.useCallback(() => {
    setIsRejectDialogOpen(false);
    actions.reject();
  }, [actions, setIsRejectDialogOpen]);

  const isInternationalCompany = React.useMemo(() => checkIfInternationalCompany(company?.countryCode), [company]);
  return (
    <DefaultActionCard
      action={action}
      items={[
        shouldShowData && {
          header: t('components.cards.identificationVerification.identificationType'),
          value: t(`enums.identityVerificationType.${action.type}`, action.type),
        },
        isInternationalCompany && {
          header: t('enums.customerOrganisationMemberDecisionType.name'),
          value: t(
            `enums.customerOrganisationMemberDecisionType.values.${action?.details?.customerOrganisationMember.decisionType}`,
            action?.details?.customerOrganisationMember.decisionType,
          ),
        },
        isInternationalCompany && {
          header: t('enums.customerOrganisationMemberStatus.name'),
          value: t(
            `enums.customerOrganisationMemberStatus.values.${action?.details?.customerOrganisationMember.decision}`,
            action?.details?.customerOrganisationMember.decision,
          ),
        },
      ]}
      headerProps={headerProps}
    >
      {showManualActions && (
        <RoleCheck requiredRole={Role.CUSTOMER_WRITE}>
          <Grid container>
            <Box display="flex" justifyContent="flex-end" width="100%">
              <BasicButton color="success" style={{ marginRight: '8px' }} onClick={toggleApproveDialogOpen}>
                {t('onboard.registration.actions.approve')}
              </BasicButton>
              <BasicButton color="error" onClick={toggleRejectDialogOpen}>
                {t('onboard.registration.actions.reject')}
              </BasicButton>
              <ConfirmActionModal
                isOpen={isApproveDialogOpen}
                message={t('onboard.registration.actions.identityVerification.approveDescription')}
                onConfirm={approve}
                onCancel={toggleApproveDialogOpen}
                buttonComponent={BasicButton}
                buttonProps={{
                  color: 'success',
                  children: t('onboard.registration.actions.identityVerification.approveButton'),
                }}
              />
              <ConfirmActionModal
                isOpen={isRejectDialogOpen}
                message={t('onboard.registration.actions.identityVerification.rejectDescription')}
                onConfirm={reject}
                onCancel={toggleRejectDialogOpen}
                buttonComponent={BasicButton}
                buttonProps={{
                  color: 'error',
                  children: t('onboard.registration.actions.identityVerification.rejectButton'),
                }}
              />
            </Box>
          </Grid>
        </RoleCheck>
      )}
    </DefaultActionCard>
  );
};
IdentityActionCard.propTypes = {
  action: PropTypes.shape({
    status: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
    createdDate: PropTypes.string.isRequired,
    details: PropTypes.shape({
      customerOrganisationMember: PropTypes.shape({
        decision: PropTypes.string,
        decisionType: PropTypes.string,
      }),
    }),
  }),
  company: PropTypes.shape({
    countryCode: PropTypes.string,
  }),
  actions: PropTypes.shape({
    approve: PropTypes.func.isRequired,
    reject: PropTypes.func.isRequired,
  }),
  showManualActions: PropTypes.bool.isRequired,
  shouldShowData: PropTypes.bool.isRequired,
  headerProps: PropTypes.object,
};
